import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  Building2,
  Plus,
  Edit2,
  Trash2,
  X,
  Sparkles
} from 'lucide-react';
import api from '../services/api';
import ConfirmDialog from '../components/ConfirmDialog';
import { Toast } from '../components/ui/Toast';
import { useLoader } from '../contexts/LoaderContext';


const DepartmentsManagement = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [deletingDepartment, setDeletingDepartment] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  const { showLoader, hideLoader } = useLoader();

  // ============================================================================
  // CHARGER LES DÉPARTEMENTS
  // ============================================================================
  
  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      showLoader();
      setLoading(true);
      const response = await api.departements.getAll();
      setDepartments(response.data || []);
    } catch (error) {
      console.error('Erreur chargement départements:', error);
      showToast('Erreur lors du chargement des départements', 'error');
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  // ============================================================================
  // INITIALISER DÉPARTEMENTS PAR DÉFAUT
  // ============================================================================
  
  const handleInitDefaults = async () => {
    if (departments.length > 0) {
      showToast('Des départements existent déjà', 'info');
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.departements.initDefaults();
      showToast(response.message || 'Départements par défaut créés', 'success');
      await loadDepartments();
    } catch (error) {
      console.error('Erreur initialisation départements:', error);
      showToast('Erreur lors de l\'initialisation', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // CRÉER / MODIFIER UN DÉPARTEMENT
  // ============================================================================
  
  const handleOpenModal = (department = null) => {
    if (department) {
      setEditingDepartment(department);
      setFormData({
        name: department.name,
        description: department.description || ''
      });
    } else {
      setEditingDepartment(null);
      setFormData({ name: '', description: '' });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingDepartment(null);
    setFormData({ name: '', description: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      showToast('Le nom du département est requis', 'error');
      return;
    }

    try {
      if (editingDepartment) {
        await api.departements.update(editingDepartment.id, formData);
        showToast('Département modifié avec succès', 'success');
      } else {
        await api.departements.create(formData);
        showToast('Département créé avec succès', 'success');
      }
      
      handleCloseModal();
      await loadDepartments();
    } catch (error) {
      console.error('Erreur:', error);
      showToast(error.message || 'Une erreur est survenue', 'error');
    }
  };

  // ============================================================================
  // SUPPRIMER UN DÉPARTEMENT
  // ============================================================================
  
  const handleDelete = async () => {
    try {
      await api.departements.delete(deletingDepartment.id);
      showToast('Département supprimé avec succès', 'success');
      setDeletingDepartment(null);
      await loadDepartments();
    } catch (error) {
      console.error('Erreur suppression:', error);
      showToast(error.message || 'Erreur lors de la suppression', 'error');
      setDeletingDepartment(null);
    }
  };

  // ============================================================================
  // TOAST
  // ============================================================================
  
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast({ show: false, message: '', type: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  // ============================================================================
  // RENDER LOADING
  // ============================================================================
  
  if (loading && departments.length === 0) {
    return null;
  }

  // ============================================================================
  // RENDER PRINCIPAL
  // ============================================================================
  
  return (
    <div className="space-y-6">
      
      {/* Header - Uniquement si des départements existent */}
      {departments.length > 0 && (
        <div className="flex items-center justify-end">
          {/* Bouton Nouveau département - À droite */}
          <button
            onClick={() => handleOpenModal()}
            className="px-4 py-2.5 text-white rounded-xl font-semibold text-sm transition-all hover:shadow-lg flex items-center gap-2"
            style={{ backgroundColor: '#448696' }}
          >
            <Plus size={18} />
            Nouveau département
          </button>
        </div>
      )}

      {/* Message vide - Uniquement les 2 boutons centrés */}
      {departments.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
          <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center"
               style={{ backgroundColor: '#44869620' }}>
            <Building2 className="w-10 h-10" style={{ color: '#448696' }} />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Aucun département
          </h3>
          <p className="text-gray-600 mb-6">
            Commencez par créer vos départements ou utilisez les départements par défaut
          </p>
          
          {/* Uniquement 2 boutons */}
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={handleInitDefaults}
              className="px-6 py-3 bg-white border-2 rounded-xl font-semibold transition-all hover:shadow-md"
              style={{ borderColor: '#fed774', color: '#d4a843' }}
            >
              <Sparkles size={18} className="inline mr-2" />
              Départements par défaut
            </button>
            <button
              onClick={() => handleOpenModal()}
              className="px-6 py-3 text-white rounded-xl font-semibold transition-all hover:shadow-lg"
              style={{ backgroundColor: '#448696' }}
            >
              <Plus size={18} className="inline mr-2" />
              Créer un département
            </button>
          </div>
        </div>
      ) : (
        /* Liste des départements */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {departments.map((dept, index) => (
            <div
              key={dept.id}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-lg transition-all"
              style={{ 
                animation: 'slideUp 0.5s ease-out forwards',
                animationDelay: `${index * 50}ms`
              }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                       style={{ backgroundColor: '#44869620' }}>
                    <Building2 size={20} style={{ color: '#448696' }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm">{dept.name}</h3>
                    {dept.description && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {dept.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
                <button
                  onClick={() => handleOpenModal(dept)}
                  className="p-2 hover:bg-gray-50 rounded-lg transition-colors"
                  title="Modifier"
                >
                  <Edit2 size={16} className="text-gray-600" />
                </button>
                <button
                  onClick={() => setDeletingDepartment(dept)}
                  className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                  title="Supprimer"
                >
                  <Trash2 size={16} className="text-red-600" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ============================================================ */}
      {/* MODAL CRÉER / MODIFIER - DANS UN PORTAIL */}
      {/* ============================================================ */}
      
      {showModal && <ModalPortal>
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 99999 }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-scaleIn">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">
                {editingDepartment ? 'Modifier le département' : 'Nouveau département'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Nom */}
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-2">
                  Nom du département <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none transition-all font-medium"
                  placeholder="Ex: IT, Ventes, Finance..."
                  onFocus={(e) => {
                    e.target.style.borderColor = '#448696';
                    e.target.style.boxShadow = '0 0 0 3px rgba(136, 185, 197, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e5e5';
                    e.target.style.boxShadow = 'none';
                  }}
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none transition-all font-medium resize-none"
                  placeholder="Ex: Technologies de l'information"
                  rows={3}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#448696';
                    e.target.style.boxShadow = '0 0 0 3px rgba(136, 185, 197, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e5e5';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Boutons */}
              <div className="flex items-center justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2.5 text-white font-semibold rounded-xl transition-all hover:shadow-lg"
                  style={{ backgroundColor: '#448696' }}
                >
                  {editingDepartment ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </ModalPortal>}

      {/* ============================================================ */}
      {/* CONFIRM DIALOG SUPPRESSION */}
      {/* ============================================================ */}
      
      <ConfirmDialog
        isOpen={!!deletingDepartment}
        onClose={() => setDeletingDepartment(null)}
        onConfirm={handleDelete}
        title="Supprimer le département ?"
        message={`Êtes-vous sûr de vouloir supprimer "${deletingDepartment?.name}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />

      {/* ============================================================ */}
      {/* TOAST */}
      {/* ============================================================ */}
      
      {toast.show && (
        <Toast
          show={toast.show}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: '' })}
        />
      )}

      {/* Animations CSS */}
      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }

        .animate-scaleIn {
          animation: scaleIn 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

// ============================================================================
// COMPOSANT PORTAL POUR LES MODALS
// ============================================================================

const ModalPortal = ({ children }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    // Bloquer le scroll du body quand le modal est ouvert
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  if (!mounted) return null;

  return ReactDOM.createPortal(
    children,
    document.body
  );
};

export default DepartmentsManagement;